import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { products } from '../mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingCart, Check, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4 text-[#1e3a5f]">Product Not Found</h1>
          <Link to="/products">
            <Button className="bg-[#1e3a5f] hover:bg-[#2c5282] text-white rounded-full">
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={`${product.name} | Kalorama Medical`}
        description={product.description}
        keywords={`${product.name}, ${product.category}, medical equipment`}
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/products"
            className="inline-flex items-center text-[#7bb5d4] hover:text-[#1e3a5f] transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-1 gap-12 mb-16">
          {/* Product Details */}
          <div className="max-w-4xl mx-auto">
            <div className="mb-4">
              <span className="text-sm font-medium text-[#7bb5d4] bg-blue-50 px-3 py-1 rounded">
                {product.category}
              </span>
            </div>
            <h1 className="text-4xl font-bold mb-6 text-[#1e3a5f]">
              {product.name}
            </h1>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {product.description}
            </p>

            {/* Features */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4 text-[#1e3a5f]">Key Features</h3>
                <ul className="space-y-3">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-[#7bb5d4] mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Link to="/contact" className="flex-1">
                <Button
                  size="lg"
                  className="w-full bg-[#1e3a5f] hover:bg-[#2c5282] text-white rounded-full text-lg py-6 transition-all duration-300 hover:scale-105"
                >
                  Request Quote
                </Button>
              </Link>
              <Link to="/contact" className="flex-1">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white rounded-full text-lg py-6 transition-all duration-300"
                >
                  Contact Us
                </Button>
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <Check className="h-6 w-6 text-[#7bb5d4] mx-auto mb-2" />
                <p className="text-xs font-medium text-gray-700">Quality Guaranteed</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <Check className="h-6 w-6 text-[#7bb5d4] mx-auto mb-2" />
                <p className="text-xs font-medium text-gray-700">Fast Shipping</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <Check className="h-6 w-6 text-[#7bb5d4] mx-auto mb-2" />
                <p className="text-xs font-medium text-gray-700">30-Day Returns</p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold mb-8 text-[#1e3a5f]">
              Related Products
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Card key={relatedProduct.id} className="group hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="mb-3">
                      <span className="text-xs font-medium text-[#7bb5d4] bg-blue-50 px-3 py-1 rounded-full">
                        {relatedProduct.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-[#1e3a5f] group-hover:text-[#7bb5d4] transition-colors duration-200">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {relatedProduct.description}
                    </p>
                    <Link to={`/product/${relatedProduct.id}`}>
                      <Button className="w-full bg-[#1e3a5f] hover:bg-[#2c5282] text-white rounded-full transition-all duration-300">
                        View Details
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
